import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  TrendingUp, Users, Eye, Target, BarChart3, 
  Tv, Smartphone, Globe, CheckCircle, ArrowRight
} from 'lucide-react';

const AdPartnerPage = () => {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: <Users className="w-8 h-8 text-sky-400" />,
      title: "Passionate Sports Fans",
      description: "Reach thousands of dedicated soccer, rugby, GAA, and hurling fans who actively engage with our content."
    },
    {
      icon: <Tv className="w-8 h-8 text-purple-400" />,
      title: "Live Stream Exposure",
      description: "Your logo featured during live match broadcasts, reaching viewers when they're most engaged."
    },
    {
      icon: <Smartphone className="w-8 h-8 text-green-400" />,
      title: "Social Media Reach",
      description: "Logo placement on highlight videos across YouTube, Instagram Reels, TikTok, and YouTube Shorts."
    },
    {
      icon: <Globe className="w-8 h-8 text-orange-400" />,
      title: "Website Presence",
      description: "Display ads across our news articles, podcast page, radio page, and video player."
    },
    {
      icon: <Target className="w-8 h-8 text-red-400" />,
      title: "Targeted Placement",
      description: "Choose exactly where your ads appear - website, streams, or social media highlights."
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-yellow-400" />,
      title: "Real-Time Analytics",
      description: "Track impressions, clicks, and ROI with our comprehensive advertiser dashboard."
    }
  ];

  const placements = [
    { name: "News Articles", cpm: "$8", type: "Website" },
    { name: "Podcast Page", cpm: "$10", type: "Website" },
    { name: "Radio Page", cpm: "$10", type: "Website" },
    { name: "Video Player", cpm: "$12", type: "Website" },
    { name: "Live Stream Overlay", cpm: "$20", type: "Broadcast" },
    { name: "YouTube (16:9)", cpm: "$15", type: "Social" },
    { name: "YouTube Shorts", cpm: "$12", type: "Social" },
    { name: "Instagram Reels", cpm: "$18", type: "Social" },
    { name: "TikTok", cpm: "$10", type: "Social" },
  ];

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-sky-900 via-gray-900 to-purple-900 py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Become an Ad Partner
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Connect your brand with passionate sports fans across our live streams, 
            website, and social media channels. Pay only for the impressions you get.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => navigate('/advertiser')}
              className="bg-sky-500 hover:bg-sky-600 text-lg px-8"
            >
              Get Started <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
              className="border-white/30 text-white hover:bg-white/10 text-lg px-8"
            >
              View Pricing
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-gray-800 py-8 border-y border-gray-700">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <div>
            <p className="text-3xl font-bold text-sky-400">100+</p>
            <p className="text-gray-400">Live Matches/Year</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-purple-400">50K+</p>
            <p className="text-gray-400">Monthly Viewers</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-green-400">4</p>
            <p className="text-gray-400">Sports Covered</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-orange-400">10K+</p>
            <p className="text-gray-400">Social Followers</p>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            Why Partner With Us?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="mb-4">{benefit.icon}</div>
                  <h3 className="text-xl font-semibold text-white mb-2">{benefit.title}</h3>
                  <p className="text-gray-400">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div id="pricing" className="py-16 px-4 bg-gray-800/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-4">
            Transparent CPM Pricing
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Pay per thousand impressions (CPM). Set your budget, choose your placements, 
            and only pay for the exposure you receive.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Website */}
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-sky-400 mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5" /> Website
                </h3>
                <ul className="space-y-3">
                  {placements.filter(p => p.type === 'Website').map((p, i) => (
                    <li key={i} className="flex justify-between text-gray-300">
                      <span>{p.name}</span>
                      <span className="text-sky-400 font-semibold">{p.cpm}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Broadcast */}
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-purple-400 mb-4 flex items-center gap-2">
                  <Tv className="w-5 h-5" /> Broadcast
                </h3>
                <ul className="space-y-3">
                  {placements.filter(p => p.type === 'Broadcast').map((p, i) => (
                    <li key={i} className="flex justify-between text-gray-300">
                      <span>{p.name}</span>
                      <span className="text-purple-400 font-semibold">{p.cpm}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <p className="text-sm text-gray-500">Video Commercials:</p>
                  <p className="text-gray-300 flex justify-between mt-2">
                    <span>30 seconds</span>
                    <span className="text-purple-400 font-semibold">$25</span>
                  </p>
                  <p className="text-gray-300 flex justify-between mt-1">
                    <span>60 seconds</span>
                    <span className="text-purple-400 font-semibold">$40</span>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Social */}
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-green-400 mb-4 flex items-center gap-2">
                  <Smartphone className="w-5 h-5" /> Social Media
                </h3>
                <ul className="space-y-3">
                  {placements.filter(p => p.type === 'Social').map((p, i) => (
                    <li key={i} className="flex justify-between text-gray-300">
                      <span>{p.name}</span>
                      <span className="text-green-400 font-semibold">{p.cpm}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          <p className="text-center text-gray-500 text-sm mt-6">
            * CPM = Cost Per Mille (per 1,000 impressions). Minimum budget: $50
          </p>
        </div>
      </div>

      {/* How It Works */}
      <div className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            How It Works
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Sign Up", desc: "Create your free advertiser account" },
              { step: "2", title: "Add Funds", desc: "Load your account with prepaid balance" },
              { step: "3", title: "Create Campaign", desc: "Set budget, dates, and placements" },
              { step: "4", title: "Go Live", desc: "Activate and track your results" }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 bg-sky-500 rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 px-4 bg-gradient-to-r from-sky-600 to-purple-600">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Grow Your Brand?
          </h2>
          <p className="text-white/80 mb-8 text-lg">
            Join local businesses already reaching sports fans through New Logical Sports.
          </p>
          <Button 
            size="lg" 
            onClick={() => navigate('/advertiser')}
            className="bg-white text-gray-900 hover:bg-gray-100 text-lg px-8"
          >
            Create Your Account <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>

      {/* FAQ */}
      <div className="py-16 px-4 bg-gray-800/50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {[
              { q: "What's the minimum budget?", a: "The minimum campaign budget is $50. You can add funds in packages of $50, $100, $250, or $500." },
              { q: "How do I upload my ad creative?", a: "After creating a campaign, you can upload images directly through your dashboard. For video commercials, contact us for assisted upload." },
              { q: "When will my ads start showing?", a: "Once you activate your campaign and it's within the date range, your ads will immediately enter rotation based on your selected placements." },
              { q: "Can I pause my campaign?", a: "Yes, you can pause and resume campaigns at any time from your dashboard." },
              { q: "How is social media impression counted?", a: "For social media placements, we track views on highlight videos where your logo appears. You'll receive impression reports after each posting cycle." }
            ].map((faq, i) => (
              <div key={i} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-sky-400" />
                  {faq.q}
                </h3>
                <p className="text-gray-400 ml-7">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdPartnerPage;
